import { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API_URL } from "@/utils/apiConfig";
import {
  Printer, Download, CheckCircle2, ExternalLink,
  Monitor, AlertTriangle, ArrowRight, Coffee
} from "lucide-react";

const API = API_URL;

export default function PrinterGuidePage() {
  const [downloading, setDownloading] = useState(false);

  const downloadInstaller = async (platform) => {
    setDownloading(true);
    try {
      const endpoint = platform === "windows" ? "installer-windows" : platform === "mac" ? "installer-mac" : "installer-linux";
      const filename = platform === "windows" ? "kaiso-impressora-v10.bat" : "kaiso-impressora-v10.sh";
      const response = await fetch(`${API}/api/qz/${endpoint}?v=10&t=${Date.now()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const blob = new Blob([text], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} baixado!`);
    } catch (e) {
      toast.error("Erro ao baixar: " + e.message);
    }
    setDownloading(false);
  };

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto p-6 space-y-6" data-testid="printer-guide-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Printer className="w-6 h-6" /> Como configurar a impressora
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Siga os passos abaixo para configurar a impressao automatica (igual Menu Integrado)
          </p>
        </div>

        {/* Pre-requisito */}
        <Card className="border-l-4 border-l-blue-500" data-testid="step-prerequisite">
          <CardContent className="py-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-gray-900">Pre-requisito</p>
                <p className="text-sm text-gray-600 mt-1">
                  O driver da sua impressora termica ja deve estar instalado no computador.
                  Se nao tem certeza, conecte a impressora via USB e o Windows deve instalar automaticamente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passo 1 */}
        <Card data-testid="step-1">
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-orange-600 font-bold text-lg">1</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Baixar e instalar o Java</h3>
                <p className="text-sm text-gray-600 mt-1">
                  O QZ Tray precisa do Java para funcionar. Baixe e instale a versao mais recente.
                </p>
                <a
                  href="https://www.java.com/pt-BR/download/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                  data-testid="download-java-btn"
                >
                  <Download className="w-4 h-4" /> Baixar Java <ExternalLink className="w-3 h-3" />
                </a>
                <p className="text-xs text-gray-400 mt-2">
                  Apos instalar, feche e abra o navegador.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passo 2 */}
        <Card data-testid="step-2">
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-orange-600 font-bold text-lg">2</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Baixar e instalar o QZ Tray</h3>
                <p className="text-sm text-gray-600 mt-1">
                  O QZ Tray e o programa que faz a ponte entre o navegador e a impressora.
                  De um duplo clique no arquivo baixado e siga a instalacao.
                </p>
                <a
                  href="https://qz.io/download/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  data-testid="download-qz-btn"
                >
                  <Download className="w-4 h-4" /> Baixar QZ Tray <ExternalLink className="w-3 h-3" />
                </a>
                <p className="text-xs text-gray-400 mt-2">
                  Se aparecer mensagem pedindo Java, volte ao Passo 1 e instale primeiro.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passo 3 - Metodo Manual (igual Menu Integrado) */}
        <Card className="border-2 border-orange-300 bg-orange-50/30" data-testid="step-3">
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Instalar o certificado Kaiso (OBRIGATORIO)</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Este e o passo mais importante! Sem o certificado do Kaiso, o QZ Tray
                  fica pedindo permissao ("Allow?") toda vez que tenta imprimir.
                </p>

                {/* Metodo Manual - Igual Menu Integrado */}
                <div className="mt-4 bg-white border-2 border-green-300 rounded-lg p-4">
                  <p className="font-bold text-green-700 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Metodo Manual (Recomendado - Igual Menu Integrado)
                  </p>
                  <ol className="list-decimal list-inside mt-3 space-y-3 text-sm text-gray-700">
                    <li>
                      <strong>Baixe o certificado:</strong>
                      <a
                        href={`${API}/api/qz/cert.pem`}
                        download="cert.pem"
                        className="ml-2 inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
                        data-testid="download-cert-pem-btn"
                      >
                        <Download className="w-3 h-3" /> Baixar cert.pem
                      </a>
                    </li>
                    <li>
                      Abra a pasta do QZ Tray:<br/>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded mt-1 inline-block">
                        C:\Program Files\QZ Tray\
                      </code>
                    </li>
                    <li>
                      <strong>Cole o cert.pem</strong> dentro dessa pasta.
                      {" "}Se ja tem um cert.pem do Menu Integrado, <strong>substitua</strong> pelo do Kaiso,
                      {" "}ou renomeie o antigo para <code className="text-xs bg-gray-100 px-1 rounded">cert_menuintegrado.pem</code>
                    </li>
                    <li>
                      Abra o arquivo <code className="text-xs bg-gray-100 px-2 py-1 rounded">qz-tray.properties</code> (na mesma pasta) com o Bloco de Notas
                    </li>
                    <li>
                      Adicione esta linha no final:<br/>
                      <code className="text-xs bg-gray-900 text-green-400 px-3 py-1.5 rounded mt-1 inline-block font-mono">
                        authcert.override=cert.pem
                      </code>
                    </li>
                    <li>
                      Salve o arquivo e <strong>reinicie o computador</strong>
                    </li>
                  </ol>
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                    <strong>IMPORTANTE:</strong> Se o qz-tray.properties ja tem a linha <code>authcert.override=</code>
                    {" "}com outro valor, apague a linha antiga e coloque a nova.
                    Nao pode ter duas linhas authcert.override!
                  </div>
                </div>

                {/* Metodo Automatico */}
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Ou use o instalador automatico:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => downloadInstaller("windows")}
                      disabled={downloading}
                      size="sm"
                      variant="outline"
                      className="gap-2 text-xs"
                      data-testid="download-cert-windows-btn"
                    >
                      <Download className="w-3 h-3" />
                      <Monitor className="w-3 h-3" /> Windows (.bat)
                    </Button>
                    <Button
                      onClick={() => downloadInstaller("mac")}
                      disabled={downloading}
                      size="sm"
                      variant="outline"
                      className="gap-2 text-xs"
                      data-testid="download-cert-mac-btn"
                    >
                      <Download className="w-3 h-3" />
                      <Coffee className="w-3 h-3" /> macOS (.sh)
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passo 4 */}
        <Card data-testid="step-4">
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-orange-600 font-bold text-lg">4</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Reiniciar o computador</h3>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>OBRIGATORIO!</strong> O QZ Tray precisa reiniciar para carregar o certificado novo.
                  Reinicie o computador e aguarde ele ligar novamente.
                </p>
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  Se o icone do QZ Tray (escudo azul) nao aparecer na barra de tarefas apos reiniciar,
                  abra manualmente: Iniciar <ArrowRight className="w-3 h-3 inline" /> QZ Tray
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passo 5 */}
        <Card data-testid="step-5">
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-orange-600 font-bold text-lg">5</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">Abrir o Kaiso System e permitir conexao</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Abra o Kaiso System no navegador. Na primeira vez, vai aparecer uma janela perguntando
                  se voce permite a conexao com o QZ Tray.
                </p>
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 space-y-1">
                  <p className="font-bold">Na janela que aparecer:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-xs">
                    <li>Marque a caixa <strong>"Remember this decision"</strong></li>
                    <li>Clique em <strong>"Allow"</strong></li>
                  </ol>
                  <p className="text-xs mt-1">Isso so precisa ser feito <strong>uma unica vez</strong>. Nunca mais vai aparecer.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pronto! */}
        <Card className="border-2 border-green-400 bg-green-50/50" data-testid="step-done">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-green-800">Pronto! Impressora configurada!</h3>
                <p className="text-sm text-green-700 mt-1">
                  Agora va ate a <strong>Tela de Pedidos</strong> ou abra o <strong>Print Station</strong>
                  e faca um pedido teste. O recibo vai sair automaticamente na impressora!
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    onClick={() => window.open("/print-station", "_blank")}
                    className="bg-green-600 hover:bg-green-700 text-white gap-2"
                    data-testid="open-print-station-btn"
                  >
                    <Printer className="w-4 h-4" /> Abrir Print Station
                  </Button>
                  <Button
                    onClick={() => window.open("/printer-setup", "_blank")}
                    variant="outline"
                    className="gap-2 border-green-300 text-green-700"
                    data-testid="open-printer-setup-btn"
                  >
                    Configuracoes avancadas
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card data-testid="troubleshooting">
          <CardContent className="py-5">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Problemas comuns
            </h3>
            <div className="mt-3 space-y-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-800">QZ Tray nao conecta</p>
                <p className="text-xs text-gray-600 mt-1">
                  Verifique se o icone do QZ Tray (escudo azul) esta na barra de tarefas.
                  Se nao estiver, abra manualmente. Se o escudo estiver vermelho ou cinza,
                  volte ao Passo 3 e execute o instalador como Administrador novamente.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-800">Popup de permissao aparece toda vez</p>
                <p className="text-xs text-gray-600 mt-1">
                  O certificado nao foi instalado corretamente. Volte ao Passo 3 e execute
                  como Administrador. Depois reinicie o computador (Passo 4).
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-800">Impressao sai cortada ou desalinhada</p>
                <p className="text-xs text-gray-600 mt-1">
                  Va em Configuracoes {'>'} Impressao e ajuste o numero de colunas.
                  Para impressoras de 80mm, use 42 colunas. Para 58mm, use 32 colunas.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-800">Ja uso Menu Integrado com QZ Tray</p>
                <p className="text-xs text-gray-600 mt-1">
                  Sem problema! O instalador Kaiso e compativel com o Menu Integrado.
                  Os dois certificados funcionam juntos sem conflito.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800" data-testid="auto-print-notice">
          <p className="font-bold">Impressao automatica</p>
          <p className="mt-1">
            Para impressao automatica de pedidos, deixe sempre uma aba aberta na tela de pedidos
            ou no <strong>Print Station</strong>. Quando chegar um pedido novo, o recibo sera
            impresso automaticamente sem precisar clicar em nada.
          </p>
        </div>
      </div>
    </MainLayout>
  );
}
